import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import Layout from '../components/Layout';
import { CreditCard, Check, ArrowLeft, Loader2, Star, Zap, Crown, ArrowUp, ArrowDown, AlertTriangle } from 'lucide-react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';

const PLAN_ICONS = { start: Star, pro: Zap, super: Crown };
// Les fonctionnalités listées par plan : clés i18n (traduites à l'affichage via t()).
const PLAN_FEATURES = {
  start: ['subscription.featStartRepairs', 'subscription.featStartClients', 'subscription.featStartQuotesInvoices', 'subscription.featStartOnePos'],
  pro: ['subscription.featProAllStart', 'subscription.featProMultiUsers', 'subscription.featProAdvancedStats', 'subscription.featProThreePos', 'subscription.featProPrioritySupport'],
  super: ['subscription.featSuperAllPro', 'subscription.featSuperUnlimitedPos', 'subscription.featSuperCustomApi', 'subscription.featSuperDedicatedSupport', 'subscription.featSuperWhiteLabel'],
};

const SubscriptionPage = () => {
  const { api } = useAuth();
  const { t, formatCurrency, formatDate } = useLanguage();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [changingPlan, setChangingPlan] = useState(null);
  const [previewData, setPreviewData] = useState(null);
  const [showConfirm, setShowConfirm] = useState(null);

  useEffect(() => {
    const changed = searchParams.get('changed');
    if (changed === '1') {
      toast.success(t('subscription.toastChanged'));
    } else if (changed === '0') {
      toast.error(t('subscription.toastCancelled'));
    }
  }, [searchParams, t]);

  const fetchSub = useCallback(async () => {
    try {
      const res = await api.get('/subscription/my');
      setData(res.data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => { fetchSub(); }, [fetchSub]);

  const handlePreviewChange = async (planKey) => {
    try {
      const res = await api.get(`/subscription/change-plan/preview?plan=${planKey}`);
      setPreviewData(res.data);
      setShowConfirm(planKey);
    } catch (err) {
      toast.error(err.response?.data?.detail || t('subscription.errorPreview'));
    }
  };

  const handleConfirmChange = async () => {
    if (!showConfirm) return;
    setChangingPlan(showConfirm);
    try {
      const res = await api.post('/subscription/change-plan', {
        plan: showConfirm,
        origin_url: window.location.origin,
      });
      if (res.data?.url) {
        window.location.href = res.data.url;
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || t('subscription.errorChange'));
    } finally {
      setChangingPlan(null);
      setShowConfirm(null);
      setPreviewData(null);
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-8 h-8 text-emerald-400 animate-spin" />
        </div>
      </Layout>
    );
  }

  const sub = data?.subscription;
  const plans = data?.plans || {};
  const currentPlan = sub?.plan || 'start';
  const planOrder = Object.keys(plans);

  return (
    <Layout>
      <div className="max-w-4xl mx-auto space-y-6" data-testid="subscription-page">
        {/* Header */}
        <div className="flex items-center gap-4">
          <button onClick={() => navigate('/dashboard')} className="p-2 rounded-lg bg-white/5 hover:bg-white/10 text-gray-400 hover:text-white transition-colors" data-testid="sub-back-btn">
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div>
            <h1 className="text-2xl font-black text-white" data-testid="subscription-title">{t('subscription.title')}</h1>
            <p className="text-gray-400 text-sm mt-1">{t('subscription.subtitle')}</p>
          </div>
        </div>

        {/* Current Subscription Info */}
        {sub && (
          <div className="rounded-xl border border-emerald-500/20 bg-emerald-500/5 p-5" data-testid="current-sub-info">
            <div className="flex items-center justify-between flex-wrap gap-4">
              <div>
                <p className="text-sm text-gray-400">{t('subscription.currentPlan')}</p>
                <p className="text-xl font-bold text-emerald-400">{sub.plan_name || currentPlan}</p>
              </div>
              <div className="text-right">
                <p className="text-sm text-gray-400">{t('subscription.status')}</p>
                <span className={`inline-block px-3 py-1 rounded-full text-xs font-bold ${sub.status === 'active' ? 'bg-emerald-500/20 text-emerald-400' : sub.status === 'trial' ? 'bg-yellow-500/20 text-yellow-400' : 'bg-red-500/20 text-red-400'}`}>
                  {sub.status === 'active' ? t('subscription.statusActive') : sub.status === 'trial' ? t('subscription.statusTrial') : sub.status}
                </span>
              </div>
              <div className="text-right">
                <p className="text-sm text-gray-400">{t('subscription.monthlyPrice')}</p>
                <p className="text-lg font-bold text-white">{formatCurrency(sub.monthly_price || 0)}{t('subscription.perMonth')}</p>
              </div>
              {sub.next_billing_date && (
                <div className="text-right">
                  <p className="text-sm text-gray-400">{t('subscription.nextBilling')}</p>
                  <p className="text-sm font-medium text-white">{formatDate(sub.next_billing_date)}</p>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Plans */}
        <div>
          <h2 className="text-lg font-bold text-white mb-4">{t('subscription.changePlan')}</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {Object.entries(plans).map(([key, plan]) => {
              const Icon = PLAN_ICONS[key] || Star;
              const isCurrent = key === currentPlan;
              const features = PLAN_FEATURES[key] || [];
              const isUpgrade = planOrder.indexOf(key) > planOrder.indexOf(currentPlan);
              const isDowngrade = planOrder.indexOf(key) < planOrder.indexOf(currentPlan);
              return (
                <div
                  key={key}
                  className={`rounded-xl border p-5 transition-all ${isCurrent ? 'border-emerald-500/50 bg-emerald-500/5 ring-1 ring-emerald-500/20' : 'border-white/10 bg-white/5 hover:border-white/20'}`}
                  data-testid={`plan-card-${key}`}
                >
                  <div className="flex items-center gap-3 mb-3">
                    <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${isCurrent ? 'bg-emerald-500/20' : 'bg-white/10'}`}>
                      <Icon className={`w-5 h-5 ${isCurrent ? 'text-emerald-400' : 'text-gray-400'}`} />
                    </div>
                    <div>
                      <h3 className="font-bold text-white">{plan.name}</h3>
                      <p className="text-lg font-black text-white">{formatCurrency(plan.price)}<span className="text-xs text-gray-500 font-normal">{t('subscription.perMonth')}</span></p>
                    </div>
                  </div>
                  <ul className="space-y-2 mb-5">
                    {features.map((f, i) => (
                      <li key={i} className="flex items-center gap-2 text-sm text-gray-300">
                        <Check className="w-3.5 h-3.5 text-emerald-400 flex-shrink-0" /> {t(f)}
                      </li>
                    ))}
                  </ul>
                  {isCurrent ? (
                    <div className="w-full py-2.5 text-center text-sm font-medium text-emerald-400 bg-emerald-500/10 rounded-lg">
                      {t('subscription.currentPlan')}
                    </div>
                  ) : (
                    <button
                      onClick={() => handlePreviewChange(key)}
                      disabled={!!changingPlan}
                      className={`w-full py-2.5 text-center text-sm font-semibold rounded-lg transition-colors disabled:opacity-50 flex items-center justify-center gap-2 ${
                        isUpgrade
                          ? 'text-white bg-emerald-600 hover:bg-emerald-500'
                          : 'text-orange-300 bg-orange-600/20 hover:bg-orange-600/30 border border-orange-500/30'
                      }`}
                      data-testid={`change-plan-btn-${key}`}
                    >
                      {changingPlan === key ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : isUpgrade ? (
                        <ArrowUp className="w-4 h-4" />
                      ) : (
                        <ArrowDown className="w-4 h-4" />
                      )}
                      {changingPlan === key ? t('subscription.redirecting') : isUpgrade ? t('subscription.upgrade') : t('subscription.downgrade')}
                    </button>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {/* Confirmation Modal */}
        {showConfirm && previewData && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm">
            <div className="bg-gray-900 border border-white/10 rounded-2xl p-6 max-w-md w-full mx-4 space-y-4" data-testid="plan-change-confirm-modal">
              <div className="flex items-center gap-3">
                <AlertTriangle className={`w-6 h-6 ${previewData.is_upgrade ? 'text-emerald-400' : 'text-orange-400'}`} />
                <h3 className="text-lg font-bold text-white">
                  {previewData.is_upgrade ? t('subscription.upgradeTitle') : t('subscription.downgradeTitle')}
                </h3>
              </div>

              <div className="bg-white/5 rounded-xl p-4 space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-gray-400">{t('subscription.currentPlan')}</span>
                  <span className="text-white font-medium">{plans[previewData.current_plan]?.name || previewData.current_plan}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-400">{t('subscription.newPlan')}</span>
                  <span className="text-white font-medium">{plans[previewData.new_plan]?.name || previewData.new_plan}</span>
                </div>
                <hr className="border-white/10" />
                <div className="flex justify-between text-sm">
                  <span className="text-gray-400">{t('subscription.newMonthlyPrice')}</span>
                  <span className="text-white font-bold">{formatCurrency(previewData.new_monthly_price)}{t('subscription.perMonth')}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-400">
                    {previewData.is_upgrade ? t('subscription.upgradeDiffNow') : t('subscription.changeFee')}
                  </span>
                  <span className={`font-bold ${previewData.is_upgrade ? 'text-emerald-400' : 'text-orange-400'}`}>
                    {formatCurrency(previewData.charge_amount)}
                  </span>
                </div>
              </div>

              {!previewData.is_upgrade && (
                <p className="text-xs text-orange-400/80 bg-orange-500/10 border border-orange-500/20 rounded-lg px-3 py-2">
                  {t('subscription.downgradeFeeNotice', { amount: formatCurrency(20) })}
                </p>
              )}

              <div className="flex gap-3">
                <button
                  onClick={() => { setShowConfirm(null); setPreviewData(null); }}
                  className="flex-1 py-2.5 text-sm font-medium text-gray-400 bg-white/5 hover:bg-white/10 rounded-lg transition-colors"
                  data-testid="plan-change-cancel-btn"
                >
                  {t('subscription.cancel')}
                </button>
                <button
                  onClick={handleConfirmChange}
                  disabled={!!changingPlan}
                  className={`flex-1 py-2.5 text-sm font-semibold text-white rounded-lg transition-colors disabled:opacity-50 flex items-center justify-center gap-2 ${
                    previewData.is_upgrade ? 'bg-emerald-600 hover:bg-emerald-500' : 'bg-orange-600 hover:bg-orange-500'
                  }`}
                  data-testid="plan-change-confirm-btn"
                >
                  {changingPlan ? <Loader2 className="w-4 h-4 animate-spin" /> : <CreditCard className="w-4 h-4" />}
                  {t('subscription.confirmWithAmount', { amount: formatCurrency(previewData.charge_amount) })}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default SubscriptionPage;
